import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Rocket, Loader2, ArrowLeft, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/apiClient";
import logo from "@/assets/logo.png";

export default function Register() {
  const { register } = useAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [resending, setResending] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { first_name, last_name, email, password } = form;
    if (!first_name || !last_name || !email || !password) {
      toast.error("Please complete all required fields");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await register({
        first_name, last_name, email, password,
        phone: form.phone || null,
      });
      setPendingEmail(email);
      toast.success("Check your inbox for the verification link.");
    } catch (err) {
      toast.error(getErrorMessage(err, "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (!pendingEmail || resending) return;
    setResending(true);
    try {
      await import("@/lib/apiClient").then((m) => m.default.post("/auth/resend-verification", { email: pendingEmail, mode: "magic" }));
      toast.success("Fresh verification link sent.");
    } catch (err) { toast.error(getErrorMessage(err, "Resend failed")); }
    finally { setResending(false); }
  };

  return (
    <div className="min-h-screen grid-overlay flex flex-col">
      <div className="px-5 sm:px-10 py-5">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="back-home">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-5 pb-16">
        <div className="w-full max-w-lg glass rounded-3xl p-8 sm:p-10 animate-in-up">
          <div className="flex flex-col items-center text-center mb-8">
            <img src={logo} alt="Pit Lane Lounge" className="h-28 w-auto object-contain mb-6" />
            <div className="chip mb-3" style={{ background: "rgba(255,46,151,0.12)", border: "1px solid rgba(255,46,151,0.4)", color: "#ff2e97" }}>
              <UserPlus className="w-3.5 h-3.5" /> New Player Registration
            </div>
            <p className="text-slate-400">{pendingEmail ? "One last step — verify your email" : "Join the battle and claim your 1,000 point bonus"}</p>
          </div>

          {pendingEmail ? (
            <div className="text-center space-y-5" data-testid="verify-inbox-card">
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "rgba(0,229,255,0.16)", border: "1px solid rgba(0,229,255,0.5)" }}>
                <Rocket className="w-7 h-7" style={{ color: "#00e5ff" }} />
              </div>
              <div>
                <h3 className="font-display text-xl font-bold text-white mb-2">Check your inbox</h3>
                <p className="text-slate-300">We&apos;ve sent a verification link to <span className="font-semibold text-white">{pendingEmail}</span>. Tap it to unlock your account.</p>
                <p className="text-slate-500 text-xs mt-3">Didn&apos;t get it? Check spam, or resend below.</p>
              </div>
              <button onClick={resend} disabled={resending} className="neon-outline-btn w-full py-3" data-testid="resend-verification-web">
                {resending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : "Resend verification link"}
              </button>
              <Link to="/login" className="block text-center text-slate-400 hover:text-white text-sm" data-testid="verify-go-login">Go to sign in</Link>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-300 mb-2">First Name</label>
                <input className="input-cyber" placeholder="John" value={form.first_name}
                  onChange={(e) => update("first_name", e.target.value)} data-testid="register-firstname" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-300 mb-2">Last Name</label>
                <input className="input-cyber" placeholder="Racer" value={form.last_name}
                  onChange={(e) => update("last_name", e.target.value)} data-testid="register-lastname" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-300 mb-2">Email Address</label>
              <input type="email" className="input-cyber" placeholder="gamer@example.com" value={form.email}
                onChange={(e) => update("email", e.target.value)} data-testid="register-email" autoComplete="email" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-300 mb-2">Phone (optional)</label>
              <input className="input-cyber" placeholder="555 123 4567" value={form.phone}
                onChange={(e) => update("phone", e.target.value)} data-testid="register-phone" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-300 mb-2">Password</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} className="input-cyber pr-12" placeholder="Create a password"
                  value={form.password} onChange={(e) => update("password", e.target.value)} data-testid="register-password"
                  autoComplete="new-password" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-cyan-400" data-testid="toggle-password">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="neon-btn w-full py-4 text-base" data-testid="register-submit">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Creating Account...</> : <><Rocket className="w-5 h-5" /> Join The Arena</>}
            </button>
          </form>
          )}

          {!pendingEmail && (
            <p className="text-center text-slate-400 mt-7">
              Already a gamer?{" "}
              <Link to="/login" className="font-bold neon-cyan-text" data-testid="go-login">Sign In</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
